import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Icon from '@/icons/icon';
import { cn } from '@/lib/utils';
import { useState, type ComponentProps } from 'react';

interface PasswordInputProps extends Omit<
    ComponentProps<typeof Input>,
    'type'
> {
    /** Classes for the show/hide eye icon (use to set the fill colour). */
    iconClassName?: string;
    /** Classes for the toggle button wrapper (position, hover colour). */
    toggleClassName?: string;
    /** Take the toggle out of the tab order (login-style forms). */
    skipToggleTab?: boolean;
}

/** Password field with a built-in show/hide toggle. */
export default function PasswordInput({
    className,
    iconClassName,
    toggleClassName,
    skipToggleTab = false,
    ...props
}: PasswordInputProps) {
    const [visible, setVisible] = useState(false);

    return (
        <div className="relative">
            <Input
                {...props}
                type={visible ? 'text' : 'password'}
                className={cn('pr-10', className)}
            />
            <Button
                variant="bare"
                type="button"
                tabIndex={skipToggleTab ? -1 : undefined}
                onClick={() => setVisible((v) => !v)}
                aria-label={visible ? 'Hide password' : 'Show password'}
                className={cn(
                    'absolute top-1/2 right-3 -translate-y-1/2 text-gray-500 hover:text-gray-700',
                    toggleClassName,
                )}
            >
                <Icon
                    iconName={visible ? 'Eye' : 'EyeOff'}
                    className={cn(
                        'h-5 w-5 cursor-pointer fill-gray-700',
                        iconClassName,
                    )}
                />
            </Button>
        </div>
    );
}
